<template>
  <v-card-round
    @click="toggleSelected"
    class="blue-grey--text"
    :color="color"
    :hover="selectable"
    :ripple="selectable"
  >
    <v-container fluid :class="{'px-2 pb-3 pt-1': xs, 'px-3': !vertical}">
      <!-- A header with the id of the QMP of the product and an options menu -->
      <v-item-card-header v-if="!hideHeader" :item="item" />
      <!-- The rest of the content of the card -->
      <v-layout :column="vertical" align-center justify-space-around>
        <!-- The image of the item -->
        <v-flex v-if="item.image" shrink style="margin-bottom: -21px;">
          <!-- A custom slot to edit the way to show the image -->
          <slot
            name="image"
            :height="computedImageHeight"
            :width="computedImageWidth"
            :src="item.image"
            :style="imageStyle"
            :xs="xs"
          >
            <!-- On click in the image this component opens a dialog to compare the pivot with this item -->
            <v-comparison-dialog v-if="comparisonDialog" :item="item">
              <template #activator="{on}">
                <v-img
                  v-on="on"
                  contain
                  :height="computedImageHeight"
                  :width="computedImageWidth"
                  :src="item.image"
                  :style="imageStyle"
                />
              </template>
            </v-comparison-dialog>
            <!-- On click in the image this component opens a dialog with the image but bigger -->
            <v-dialog v-else content-class="item-card-image-content" lazy width="500">
              <template #activator="{ on }">
                <v-img
                  v-on="on"
                  contain
                  :height="computedImageHeight"
                  :width="computedImageWidth"
                  :src="item.image"
                  :style="imageStyle"
                />
              </template>
              <v-img contain :src="item.image" width="500" height="500" />
            </v-dialog>
          </slot>
        </v-flex>
        <!-- The information of the item -->
        <v-flex shrink>
          <!-- The name of the seller and its history id -->
          <v-layout justify-center>
            <v-flex class="caption" shrink> {{ homologedItemsLength }} {{ sellerName }} - {{ item.historyId }}</v-flex>
          </v-layout>
          <!-- Brand and similarity -->
          <v-layout align-center :class="{'mt-2': xs}">
            <v-flex :class="{ headline: !xs }">{{ item.brand }}</v-flex>
            <v-card-product-probability shrink
              v-if="item.similarity"
              :probability="item.similarity"
            />
          </v-layout>
          <!-- Name and URL -->
          <v-layout align-center>
            <v-flex :class="['font-weight-light my-2', {title: !xs}]">{{ item.name }}</v-flex>
            <v-flex shrink v-if="!vertical">
              <v-btn icon @click.stop="openUrl" :disabled="!item.url">
                <v-icon color="blue">open_in_new</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
          <!-- Price and URL -->
          <v-layout align-center>
            <v-flex :class="['text--darken-2', { headline: !xs }]">
              <v-item-card-price :item="item" :qmp-cover-page="qmpCoverPage" />
            </v-flex>
            <v-flex shrink v-if="vertical">
              <v-btn class="ma-0" icon @click.stop="openUrl" :disabled="!item.url">
                <v-icon color="blue">open_in_new</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
          <!-- The LastPriceUpdate -->
          <v-item-card-last-price-update :item="item" :qmp-cover-page="qmpCoverPage" />
          <!-- More content with a slot -->
          <slot />
        </v-flex>
      </v-layout>
    </v-container>
  </v-card-round>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
const { mapMutations, mapState } = createNamespacedHelpers("matching");

import VCardRound from "@/components/cards/CardRound";
import VCardProductProbability from "@/components/cards/CardProductProbability";
import VComparisonDialog from "./ComparisonDialog";
import VItemCardHeader from "./ItemCardHeader";
import VItemCardLastPriceUpdate from "./ItemCardLastPriceUpdate";
import VItemCardPrice from "./ItemCardPrice";

export default {
  components: {
    VCardRound,
    VComparisonDialog,
    VCardProductProbability,
    VItemCardHeader,
    VItemCardLastPriceUpdate,
    VItemCardPrice
  },
  props: {
    comparisonDialog: {
      type: Boolean,
      default: false
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    hideMatchesButton: {
      type: Boolean,
      default: false
    },
    imageHeight: {
      type: Number,
      default: 200
    },
    imageWidth: {
      type: Number,
      default: 200
    },
    item: {
      type: Object,
      required: true
    },
    /* Do not change the color of the card in any way */
    noColor: {
      type: Boolean,
      default: false
    },
    /* This indicates that the item is the cover page of its QMP, so it's the item that is shown in the
    similar items grid and with a click in this item it must select/unselect all the QMIs of this QMP */
    qmpCoverPage: {
      type: Boolean,
      default: false
    },
    /* Make the card selectable and it can set the "selected" property of the item */
    selectable: {
      type: Boolean,
      default: false
    },
    /* Set the image with the info in vertical aspect */
    vertical: {
      type: Boolean,
      default: false
    },
    /* Make the card extra small */
    xs: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState(["similarItemsQmps"]),
    color() {
      if (!this.selectable || this.noColor) return "";
      if (this.item.selected && !this.qmp) return "light-green lighten-3";
      if (this.qmp) {
        if (this.qmp.items.every(item => item.selected)) {
          return "light-green lighten-3";
        }
        if (this.qmp.items.some(item => item.selected)) {
          return "yellow lighten-3";
        }
      }
      return "";
    },
    computedImageHeight() {
      return this.xs ? this.imageHeight / 2 : this.imageHeight;
    },
    computedImageWidth() {
      return this.xs ? this.imageWidth / 2 : this.imageWidth;
    },
    imageStyle() {
      return {
        cursor: "pointer"
      };
    },
    qmp() {
      return this.similarItemsQmps[this.item.productId];
    },
    homologedItemsLength() {
      const length = this.item.homologedItems && this.item.homologedItems.length + 1;
      
      return length > 1 ? `(${length})` : ''
    },
    sellerName() {
      return this.item.sellerGroupTag ? this.item.sellerGroupTag : this.item.sellerName
    }
  },
  methods: {
    ...mapMutations(["setSimilarItemSelected"]),
    openUrl() {
      window.open(this.item.url, "_blank");
    },
    toggleSelected() {
      if (!this.selectable) return;
      const selected = !this.item.selected;
      const item = this.item;
      this.setSimilarItemSelected({ item, selected });
      /* Select the items of the same QMP */
      if (this.qmp && this.qmpCoverPage) {
        this.qmp.items.forEach(item => {
          if (
            item.sellerId !== this.item.sellerId ||
            item.historyId !== this.item.historyId
          ) {
            this.setSimilarItemSelected({ item, selected });
          }
        });
      }
    }
  }
};
</script>

<style>
.item-card-image-content {
  background-color: black;
}
</style>